import { useMemo } from "react";

import {
    Background,
    Controls,
    Node,
    ReactFlow,
    ReactFlowProvider
} from "@xyflow/react";

import "@xyflow/react/dist/style.css";

import styles from "./CompanyOrgStructure.module.css";
import { OrgStructureNode } from "./OrgStructureNode";
import { CompanyOrgStructureDocument } from "./orgStructureTypes";
import { OrgStructureNodeData, orgStructureToFlow } from "./orgStructureToFlow";

const nodeTypes = {
    leadership: OrgStructureNode,
    division: OrgStructureNode,
    unit: OrgStructureNode,
    team: OrgStructureNode,
    key_persons: OrgStructureNode
};

interface OrgStructureCanvasProps {
    document: CompanyOrgStructureDocument;
    selectedNodeId: string | null;
    onNodeSelect: (nodeId: string) => void;
    variant?: "inline" | "fullscreen";
    readOnly?: boolean;
}

function OrgStructureCanvasInner({
    document,
    selectedNodeId,
    onNodeSelect,
    variant = "inline",
    readOnly = false
}: OrgStructureCanvasProps) {
    const { nodes, edges } = useMemo(
        () => orgStructureToFlow(document, selectedNodeId),
        [document, selectedNodeId]
    );

    const containerClass =
        variant === "fullscreen" ? styles.canvasContainerFullscreen : styles.canvasContainer;

    return (
        <div className={containerClass}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                nodesConnectable={false}
                edgesReconnectable={false}
                elementsSelectable={!readOnly}
                nodesDraggable={false}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                panOnScroll
                zoomOnScroll
                onNodeClick={
                    readOnly
                        ? undefined
                        : (_event, node: Node<OrgStructureNodeData>) => {
                              onNodeSelect(node.data.nodeId);
                          }
                }
                proOptions={{ hideAttribution: true }}
            >
                <Background gap={16} size={1} />
                <Controls showInteractive={false} />
            </ReactFlow>
        </div>
    );
}

export function OrgStructureCanvas(props: OrgStructureCanvasProps) {
    return (
        <ReactFlowProvider>
            <OrgStructureCanvasInner {...props} />
        </ReactFlowProvider>
    );
}
